import Image from "next/image";
import Link from "next/link";
import { ArrowUpRight, FileText } from "lucide-react";
import { BrowserFrame, PhoneFrame } from "@/components/mockups/frames";
import { referenceShot, REFERENCE_SITES } from "@/lib/images/references";

/**
 * Echte Referenz: Screenshot der Kundenseite im Browser-Rahmen, optional mit
 * Smartphone-Ansicht, verlinkt auf die Live-Seite und – sobald vorhanden –
 * auf die Fallstudie (Auftrag 2026-10-28, Abschnitt 1.5 a). Rendert nichts,
 * wenn das Bild fehlt.
 */
export function ReferenceShot({
  refKey,
  caseHref,
  summary,
  showMobile = true,
  priority = false,
  className = "",
}: {
  refKey: string;
  caseHref?: string | null;
  /** Ein Satz, was die Seite für den Kunden tut (aus `portfolio_projects.summary`). */
  summary?: string | null;
  showMobile?: boolean;
  priority?: boolean;
  className?: string;
}) {
  const site = REFERENCE_SITES.find((r) => r.key === refKey);
  const files = referenceShot(refKey);
  if (!site || !files) return null;
  const host = site.url.replace(/^https?:\/\//, "");
  const alt = `Startseite von ${site.name}, umgesetzt von DMD Studio`;

  return (
    <figure className={`relative ${className}`}>
      <div
        className="relative rounded-2xl border p-4 sm:p-5"
        style={{ borderColor: "var(--border)", backgroundColor: "var(--bg-secondary)" }}
      >
        <BrowserFrame host={host} className={showMobile && files.mobile ? "sm:mr-16" : ""}>
          <div className="relative aspect-[16/10]">
            <Image
              src={files.desktop}
              alt={alt}
              fill
              priority={priority}
              sizes="(min-width: 1024px) 560px, (min-width: 640px) 50vw, 100vw"
              className="object-cover object-top"
            />
          </div>
        </BrowserFrame>
        {showMobile && files.mobile && (
          <PhoneFrame className="absolute -bottom-3 right-3 hidden shadow-xl sm:block" width={132}>
            <div className="relative h-full w-full">
              <Image src={files.mobile} alt="" fill sizes="132px" className="object-cover object-top" aria-hidden="true" />
            </div>
          </PhoneFrame>
        )}
      </div>
      <figcaption className="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm" style={{ color: "var(--text-tertiary)" }}>
        <span>
          <span className="font-semibold" style={{ color: "var(--text)" }}>{site.name}</span>
          {site.industry ? ` · ${site.industry}` : ""}
          {site.location ? ` · ${site.location}` : ""}
          {summary ? ` – ${summary}` : ""}
        </span>
        <span className="flex items-center gap-3">
          {caseHref && (
            <Link href={caseHref} className="inline-flex items-center gap-1 font-semibold text-brand-500 hover:underline">
              <FileText className="h-3.5 w-3.5" aria-hidden="true" /> Fallstudie
            </Link>
          )}
          <a href={site.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-semibold text-brand-500 hover:underline">
            Website ansehen (öffnet in neuem Tab)
            <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
          </a>
        </span>
      </figcaption>
    </figure>
  );
}
